import { ProgramCategory } from 'models/programs.types';

import { GenericEligibilityCheckResponse } from './usePaywall.types';

type PaymentProfileLike = {
  chargifyPaymentProfileId?: string;
} | null;

export type PaywallResolution =
  | { type: 'open-payment-method' }
  | { link: string; type: 'open-link' }
  | { programCategory: ProgramCategory; type: 'program-paywall' }
  | { programIds: string[]; type: 'program-resume' }
  | { type: 'noop' };

export const hasPaymentMethodOnFile = (paymentProfiles?: PaymentProfileLike[] | null): boolean =>
  !!paymentProfiles?.some((profile) => !!profile?.chargifyPaymentProfileId);

export const getEligibilityAction = (
  status: unknown
): GenericEligibilityCheckResponse['data']['action'] | undefined => {
  if (!status || status === 'OK' || typeof status !== 'object') {
    return undefined;
  }
  if (!('data' in status)) {
    return undefined;
  }
  const data = (status as { data?: unknown }).data;
  if (!data || typeof data !== 'object' || !('action' in data)) {
    return undefined;
  }
  return (data as GenericEligibilityCheckResponse['data']).action;
};

export const hasEligibilityAccess = (status: unknown, hasPaymentMethod: boolean): boolean => {
  if (status === 'OK') {
    return true;
  }
  return getEligibilityAction(status)?.type === 'CHECKOUT' && hasPaymentMethod;
};

export const resolvePaywallError = (
  error: unknown,
  hasPaymentMethod: boolean
): PaywallResolution => {
  const action = getEligibilityAction(error);

  if (!action || action.type === 'CHECKOUT') {
    return hasPaymentMethod ? { type: 'noop' } : { type: 'open-payment-method' };
  }

  if (action.type === 'OPEN_LINK') {
    return { link: action.link, type: 'open-link' };
  }

  if (action.type === 'SHOW_PAYWALL') {
    switch (action.paywall.code) {
      case 'program_paywall':
        return {
          programCategory: action.paywall.programCategory,
          type: 'program-paywall'
        };
      case 'program_resume':
        return { programIds: action.paywall.programIds, type: 'program-resume' };
      case 'billing_past_due':
        return { type: 'open-payment-method' };
      default:
        return { type: 'noop' };
    }
  }

  return { type: 'noop' };
};
